import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { tendersAPI, aiAPI } from "../services/api";
import { Shield, CheckCircle, XCircle, AlertTriangle, ArrowLeft, Zap, RefreshCw } from "lucide-react";
import toast from "react-hot-toast";

const COMPLIANCE_ITEMS = [
  { id: "cac", label: "CAC Registration", desc: "Company registered with Corporate Affairs Commission", category: "Legal" },
  { id: "tin", label: "Tax Identification Number (TIN)", desc: "Valid TIN from FIRS", category: "Legal" },
  { id: "insurance", label: "Insurance Certificates", desc: "Professional indemnity and public liability insurance", category: "Legal" },
  { id: "pencom", label: "PENCOM Compliance", desc: "Pension remittance compliance certificate", category: "Regulatory" },
  { id: "nsitf", label: "NSITF Certificate", desc: "Nigeria Social Insurance Trust Fund compliance", category: "Regulatory" },
  { id: "itf", label: "ITF Compliance", desc: "Industrial Training Fund compliance certificate", category: "Regulatory" },
  { id: "bpp", label: "BPP Registration", desc: "Registered on Bureau of Public Procurement portal", category: "Procurement" },
  { id: "audited_accounts", label: "Audited Financial Statements", desc: "3 years audited accounts by registered auditor", category: "Financial" },
  { id: "bank_reference", label: "Bank Reference Letter", desc: "Letter from company bankers confirming financial standing", category: "Financial" },
  { id: "iso9001", label: "ISO 9001 Certification", desc: "Quality management system certification", category: "Quality" },
  { id: "company_profile", label: "Company Profile Document", desc: "Detailed company profile with past projects", category: "Documentation" },
  { id: "technical_capacity", label: "Technical Capacity Evidence", desc: "Equipment list, staff qualifications, past similar works", category: "Technical" },
];

export default function ComplianceChecker() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tender, setTender] = useState(null);
  const [checklist, setChecklist] = useState({});
  const [aiAnalysis, setAiAnalysis] = useState("");
  const [analyzing, setAnalyzing] = useState(false);

  useEffect(() => {
    tendersAPI.get(id).then(res => setTender(res.data)).catch(() => navigate("/tenders"));
    const saved = localStorage.getItem(`compliance_${id}`);
    if (saved) setChecklist(JSON.parse(saved));
  }, [id, navigate]);

  const toggleItem = (itemId) => {
    const updated = { ...checklist, [itemId]: !checklist[itemId] };
    setChecklist(updated);
    localStorage.setItem(`compliance_${id}`, JSON.stringify(updated));
  };

  const completedCount = Object.values(checklist).filter(Boolean).length;
  const totalCount = COMPLIANCE_ITEMS.length;
  const scorePercent = Math.round((completedCount / totalCount) * 100);
  const scoreColor = scorePercent >= 80 ? "var(--accent-green)" : scorePercent >= 50 ? "var(--accent-orange)" : "var(--accent-red)";
  const categories = [...new Set(COMPLIANCE_ITEMS.map(i => i.category))];

  const analyzeWithAI = async () => {
    setAnalyzing(true);
    setAiAnalysis("");
    const completedItems = COMPLIANCE_ITEMS.filter(i => checklist[i.id]).map(i => i.label);
    const missingItems = COMPLIANCE_ITEMS.filter(i => !checklist[i.id]).map(i => i.label);
    try {
      const response = await aiAPI.complianceCheck({
        tender_id: Number(id),
        completed_items: completedItems,
        missing_items: missingItems,
      });
      setAiAnalysis(response.data.analysis || "");
      toast.success("Analysis complete!");
    } catch { toast.error("Analysis failed"); }
    finally { setAnalyzing(false); }
  };

  if (!tender) return <div style={{ textAlign: "center", padding: "60px", color: "var(--text-muted)" }}>Loading...</div>;

  return (
    <div style={{ maxWidth: "960px" }}>
      <button onClick={() => navigate(`/tenders/${id}`)} style={{ display: "flex", alignItems: "center", gap: "8px", background: "none", border: "none", color: "var(--text-secondary)", cursor: "pointer", fontSize: "14px", marginBottom: "24px", padding: 0 }}>
        <ArrowLeft size={16} /> Back to Tender
      </button>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px" }}>
        <div style={{ width: "40px", height: "40px", background: "rgba(79,142,247,0.1)", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Shield size={20} color="var(--accent-blue)" />
        </div>
        <div>
          <h1 style={{ fontSize: "24px" }}>Compliance Score Checker</h1>
          <p style={{ color: "var(--text-secondary)", fontSize: "13px" }}>Track your bid compliance readiness with AI analysis</p>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px", marginBottom: "24px" }}>
        <div className="card" style={{ textAlign: "center", borderColor: scoreColor }}>
          <div style={{ fontSize: "52px", fontFamily: "Syne", fontWeight: "800", color: scoreColor, lineHeight: 1 }}>{scorePercent}%</div>
          <div style={{ fontSize: "13px", color: "var(--text-secondary)", marginTop: "6px" }}>Compliance Score</div>
          <div style={{ marginTop: "12px", height: "6px", background: "var(--bg-secondary)", borderRadius: "3px" }}>
            <div style={{ height: "100%", width: `${scorePercent}%`, background: scoreColor, borderRadius: "3px", transition: "width 0.3s" }} />
          </div>
        </div>
        <div className="card" style={{ textAlign: "center" }}>
          <div style={{ fontSize: "36px", fontFamily: "Syne", fontWeight: "800", color: "var(--accent-green)" }}>{completedCount}</div>
          <div style={{ fontSize: "13px", color: "var(--text-secondary)", marginTop: "6px" }}>Items Completed</div>
        </div>
        <div className="card" style={{ textAlign: "center" }}>
          <div style={{ fontSize: "36px", fontFamily: "Syne", fontWeight: "800", color: "var(--accent-red)" }}>{totalCount - completedCount}</div>
          <div style={{ fontSize: "13px", color: "var(--text-secondary)", marginTop: "6px" }}>Items Missing</div>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 360px", gap: "24px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {categories.map(category => (
            <div key={category} className="card">
              <h3 style={{ fontSize: "13px", color: "var(--text-muted)", marginBottom: "14px", textTransform: "uppercase", letterSpacing: "0.05em" }}>{category}</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {COMPLIANCE_ITEMS.filter(i => i.category === category).map(item => (
                  <div key={item.id} onClick={() => toggleItem(item.id)} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "12px", borderRadius: "10px", cursor: "pointer", background: checklist[item.id] ? "var(--accent-green-dim)" : "var(--bg-secondary)", border: `1px solid ${checklist[item.id] ? "rgba(0,212,161,0.2)" : "var(--border)"}`, transition: "all 0.15s" }}>
                    {checklist[item.id] ? <CheckCircle size={18} color="var(--accent-green)" style={{ flexShrink: 0 }} /> : <XCircle size={18} color="var(--text-muted)" style={{ flexShrink: 0 }} />}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: "13px", fontWeight: "600", color: checklist[item.id] ? "var(--accent-green)" : "var(--text-primary)" }}>{item.label}</div>
                      <div style={{ fontSize: "11px", color: "var(--text-muted)", marginTop: "2px" }}>{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div className="card" style={{ background: "linear-gradient(135deg, rgba(79,142,247,0.08), transparent)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
              <Zap size={16} color="var(--accent-blue)" />
              <span style={{ fontSize: "14px", fontWeight: "700" }}>AI Compliance Analysis</span>
            </div>
            <p style={{ fontSize: "13px", color: "var(--text-secondary)", marginBottom: "16px", lineHeight: "1.6" }}>Get AI-powered analysis of your compliance gaps specific to this tender.</p>
            <button className="btn btn-primary" onClick={analyzeWithAI} disabled={analyzing} style={{ width: "100%", justifyContent: "center", background: "var(--accent-blue)" }}>
              {analyzing ? <><RefreshCw size={14} style={{ animation: "spin 1s linear infinite" }} /> Analyzing...</> : <><Zap size={14} /> Analyze Now</>}
            </button>
          </div>
          {aiAnalysis && (
            <div className="card">
              <h3 style={{ fontSize: "14px", marginBottom: "12px", color: "var(--accent-blue)" }}>AI Analysis Results</h3>
              <pre style={{ fontFamily: "DM Sans, sans-serif", fontSize: "12px", lineHeight: "1.8", color: "var(--text-secondary)", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{aiAnalysis}</pre>
            </div>
          )}
          {scorePercent < 80 && (
            <div className="card" style={{ background: "var(--accent-orange-dim)", borderColor: "var(--accent-orange)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                <AlertTriangle size={16} color="var(--accent-orange)" />
                <span style={{ fontSize: "13px", fontWeight: "700", color: "var(--accent-orange)" }}>Bid Risk Warning</span>
              </div>
              <p style={{ fontSize: "12px", color: "var(--text-secondary)", lineHeight: "1.6" }}>Your compliance score is below 80%. Missing items may disqualify your bid.</p>
            </div>
          )}
        </div>
      </div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
